import { groupBy, partition } from 'lodash';
import { Fragment, useMemo } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components-lib/AccordionInline/AccordionInline';
import { ExtractionType, MatchLevel } from 'dashboard/models/document';
import {
  DocumentViewerExtraction,
  FocusedExtraction,
} from 'dashboard/redux/reducers/document-viewer-document-generic';
import { Z_INDICES } from 'dashboard/styles/variables';
import ExtractionCard from 'dashboard/views/DashboardDocumentViewerView/ExtractionCard';
import Spacer from 'design-system/components/Spacer';

export interface ExtractionSidebarProps {
  extractions: DocumentViewerExtraction[];
  focusedExtractions: FocusedExtraction[];
  setFocusedExtractions: (focusedExtractions: FocusedExtraction[]) => void;
}

const ExtractionSidebarContainer = styled('div')`
  flex: 3 3 15%;
  min-width: 300px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  scrollbar-gutter: stable;
  overflow: hidden;
  &:hover {
    overflow: auto;
  }
  z-index: ${Z_INDICES.withShadow};
`;

const compareExtractions = (
  extractionGroups: DocumentViewerExtraction[],
  otherExtractionGroups: DocumentViewerExtraction[],
) => {
  const extractionGroupsType = extractionGroups[0].extractionGroup.extractionType;
  const otherExtractionGroupType = otherExtractionGroups[0].extractionGroup.extractionType;
  if (extractionGroupsType === otherExtractionGroupType) {
    const fullMatchResults = extractionGroups
      .filter((group) => group.extractionGroup.extractionMatchLevel === MatchLevel.Full)
      .reduce((acc, group) => group.results.length + acc, 0);
    const otherFullMatchResults = otherExtractionGroups
      .filter((group) => group.extractionGroup.extractionMatchLevel === MatchLevel.Full)
      .reduce((acc, group) => group.results.length + acc, 0);
    const matchResults = extractionGroups.reduce((acc, group) => group.results.length + acc, 0);
    const otherMatchResults = otherExtractionGroups.reduce(
      (acc, group) => group.results.length + acc,
      0,
    );
    return otherFullMatchResults - fullMatchResults || otherMatchResults - matchResults;
  } else if (extractionGroupsType === ExtractionType.Guided) {
    return -1;
  } else if (otherExtractionGroupType === ExtractionType.Guided) {
    return 1;
  } else {
    return 0;
  }
};

const ExtractionSidebar = ({
  extractions,
  focusedExtractions,
  setFocusedExtractions,
}: ExtractionSidebarProps) => {
  // extraction sidebar is grouped by field name and then extraction group
  const [notFilteredExtractions, filteredExtractions] = useMemo(
    () => partition(extractions, (extraction) => !extraction.extractionGroup.filtered),
    [extractions],
  );
  const nonFilteredExtractionsGroupedByFieldName = useMemo(
    () =>
      Object.entries(groupBy(notFilteredExtractions, (e) => e.extractionGroup.fieldName)).sort(
        ([_fieldName, extractionGroups], [_otherFieldname, otherExtractionGroups]) =>
          compareExtractions(extractionGroups, otherExtractionGroups),
      ),
    [notFilteredExtractions],
  );
  const filteredExtractionsGroupedByFieldName = useMemo(
    () =>
      Object.entries(groupBy(filteredExtractions, (e) => e.extractionGroup.fieldName)).sort(
        ([_fieldName, extractionGroups], [_otherFieldname, otherExtractionGroups]) =>
          compareExtractions(extractionGroups, otherExtractionGroups),
      ),
    [filteredExtractions],
  );

  return (
    <ExtractionSidebarContainer>
      {nonFilteredExtractionsGroupedByFieldName.map(([fieldName, groupedExtractions], index) => (
        <Fragment key={fieldName}>
          {!!index && <Spacer height={2} />}
          <ExtractionCard
            extractionGroups={groupedExtractions.map((e) => e.extractionGroup)}
            key={fieldName}
            focusedExtractions={focusedExtractions}
            setFocusedExtractions={setFocusedExtractions}
          />
        </Fragment>
      ))}
      {filteredExtractionsGroupedByFieldName.length > 0 && (
        <Accordion
          title={<div>{`Filtered (${filteredExtractionsGroupedByFieldName.length})`}</div>}
          defaultExpanded={false}
        >
          {filteredExtractionsGroupedByFieldName.map(([fieldName, groupedExtractions], index) => (
            <Fragment key={fieldName}>
              {!!index && <Spacer height={2} />}
              <ExtractionCard
                extractionGroups={groupedExtractions.map((e) => e.extractionGroup)}
                key={fieldName}
                focusedExtractions={focusedExtractions}
                setFocusedExtractions={setFocusedExtractions}
              />
            </Fragment>
          ))}
        </Accordion>
      )}
    </ExtractionSidebarContainer>
  );
};

export default ExtractionSidebar;
